import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EChartsOption, LineSeriesOption } from 'echarts';
import { NgxEchartsDirective } from 'ngx-echarts';

@Component({
  selector: 'lib-mdb-power-chart',
  standalone: true,
  imports: [MatCardModule, NgxEchartsDirective, TranslateModule],
  templateUrl: './mdb-power-chart.component.html',
  styleUrl: './mdb-power-chart.component.scss',
})
export class MdbPowerChartComponent implements OnChanges {
  @Input() categories: string[] = [];
  @Input() series: LineSeriesOption[] = [];

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  chartOption: EChartsOption = {};

  constructor() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.createChartOption();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['categories'] || changes['series']) {
      this.createChartOption();
    }
  }

  private createChartOption(): void {
    this.chartOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
        },
        valueFormatter: (value) =>
          `${Number(value).toLocaleString('en-US')} kW`,
      },

      legend: {
        type: 'scroll',
        top: 0,
        right: 4,
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 8,
        itemGap: 12,
        textStyle: {
          fontSize: 9,
        },
      },

      grid: {
        left: 42,
        right: 12,
        top: 32,
        bottom: 30,
        containLabel: true,
      },

      xAxis: {
        type: 'category',

        data: this.categories,

        boundaryGap: false,

        axisLabel: {
          fontSize: 9,
          margin: 8,
          hideOverlap: true,
        },

        axisLine: {
          lineStyle: {
            color: '#d9dee7',
          },
        },

        axisTick: {
          show: false,
        },
      },

      yAxis: {
        type: 'value',

        name: 'kW',

        nameLocation: 'end',

        nameTextStyle: {
          fontSize: 9,
          padding: [0, 0, 0, 8],
        },

        axisLabel: {
          fontSize: 9,
        },

        axisLine: {
          show: false,
        },

        splitLine: {
          lineStyle: {
            color: '#edf0f4',
          },
        },
      },

      series: this.series.map((item) => ({
        ...item,

        type: 'line',

        smooth: false,

        symbol: 'none',

        lineStyle: {
          width: 1.2,
          ...(item.lineStyle ?? {}),
        },

        emphasis: {
          focus: 'series',
        },
      })),
    };
  }
}
